import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TenantInvitation } from '@peruselab/api-client/tenants';
import { Icon } from '../components/Icon';
import { useAnchoredPopover } from '../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../hooks/useDismissableOverlay';
import { accountSettingsUrl } from '../auth/authClient';
import { useActiveTenant } from './activeTenant';

const SWITCHER_ESTIMATED_WIDTH_PX = 260;

/**
 * #1051 (P6) -- the top bar's tenant switcher, beside the account menu: who the app is acting for
 * (the personal tenant or an organisation), the user's other organisations with their role,
 * pending invitations to accept or decline, and the way to the organisations page. A dot on the
 * trigger says invitations are waiting. Switching changes what the lists and new saves belong to;
 * an open drawing keeps saving where it belongs.
 */
export function TenantSwitcher() {
  const { t } = useTranslation();
  const { tenants, active, invitations, switchTo, respond } = useActiveTenant();
  const [open, setOpen] = useState(false);
  const [answering, setAnswering] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const anchor = useAnchoredPopover(open, triggerRef, menuRef, () => SWITCHER_ESTIMATED_WIDTH_PX);
  useDismissableOverlay(open, setOpen, triggerRef, menuRef);

  const activeName = active ? active.name : t('tenancy.personal');
  const label = t('tenancy.switcher.label', { name: activeName });

  const choose = (tenantId: string | null) => {
    switchTo(tenantId);
    setOpen(false);
  };

  const answer = async (invitation: TenantInvitation, accept: boolean) => {
    setAnswering(invitation.id);
    setError(null);
    try {
      await respond(invitation, accept);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('tenancy.invitations.failed'));
    } finally {
      setAnswering(null);
    }
  };

  return (
    <div className="workspace-account tenant-switcher">
      <button
        ref={triggerRef}
        type="button"
        className="icon-button tenant-switcher-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={
          invitations.length > 0
            ? t('tenancy.switcher.labelWithInvitations', { name: activeName, count: invitations.length })
            : label
        }
        title={label}
        onClick={() => setOpen((value) => !value)}
        data-testid="tenant-switcher-trigger"
      >
        <Icon name={active ? 'corporateFare' : 'accountCircle'} />
        <span className="tenant-switcher-name" dir="auto">
          {activeName}
        </span>
        {invitations.length > 0 ? <span className="tenant-switcher-dot" aria-hidden="true" /> : null}
      </button>
      {open && anchor ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t('tenancy.switcher.menu')}
          className="workspace-account-menu tenant-switcher-menu"
          style={{ top: anchor.top, left: anchor.left }}
          data-testid="tenant-switcher-menu"
        >
          <p className="tenant-switcher-heading">{t('tenancy.switcher.actFor')}</p>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={active === null}
            className="tenant-switcher-option"
            onClick={() => choose(null)}
          >
            <Icon name="accountCircle" size="1.1rem" />
            <span className="tenant-switcher-option-name">{t('tenancy.personal')}</span>
          </button>
          {tenants.map((tenant) => (
            <button
              key={tenant.id}
              type="button"
              role="menuitemradio"
              aria-checked={active?.id === tenant.id}
              className={`tenant-switcher-option${tenant.type === 'SUB_ORG' ? ' tenant-switcher-option--sub' : ''}`}
              onClick={() => choose(tenant.id)}
            >
              <Icon name="corporateFare" size="1.1rem" />
              <span className="tenant-switcher-option-name" dir="auto">
                {tenant.name}
              </span>
              <span className="tenant-switcher-role">{t(`tenancy.roles.${tenant.role}`)}</span>
            </button>
          ))}

          {invitations.length > 0 ? (
            <div className="tenant-switcher-invitations" role="group" aria-label={t('tenancy.invitations.heading')}>
              <p className="tenant-switcher-heading">
                <Icon name="mail" size="1rem" />
                <span>{t('tenancy.invitations.heading')}</span>
              </p>
              {invitations.map((invitation) => (
                <div key={invitation.id} className="tenant-switcher-invitation" data-testid="tenant-invitation">
                  <span dir="auto">
                    {t('tenancy.invitations.summary', {
                      tenant: invitation.tenantName,
                      role: t(`tenancy.roles.${invitation.role}`),
                      inviter: invitation.invitedBy
                    })}
                  </span>
                  <span className="tenant-switcher-invitation-actions">
                    <button
                      type="button"
                      role="menuitem"
                      className="icon-button"
                      disabled={answering !== null || invitation.canAccept === false}
                      aria-label={t('tenancy.invitations.accept', { tenant: invitation.tenantName })}
                      title={
                        invitation.canAccept === false
                          ? t('tenancy.invitations.verifyFirst')
                          : t('tenancy.invitations.accept', { tenant: invitation.tenantName })
                      }
                      onClick={() => void answer(invitation, true)}
                    >
                      <Icon name="check" size="1.1rem" />
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="icon-button"
                      disabled={answering !== null}
                      aria-label={t('tenancy.invitations.decline', { tenant: invitation.tenantName })}
                      title={t('tenancy.invitations.decline', { tenant: invitation.tenantName })}
                      onClick={() => void answer(invitation, false)}
                    >
                      <Icon name="close" size="1.1rem" />
                    </button>
                  </span>
                </div>
              ))}
              {invitations.some((invitation) => invitation.canAccept === false) ? (
                <p className="tenant-switcher-verify" data-testid="tenant-invitation-verify">
                  <span>{t('tenancy.invitations.verifyFirst')}</span>{' '}
                  <a href={accountSettingsUrl()} target="_blank" rel="noopener" role="menuitem">
                    {t('tenancy.invitations.verifyLink')}
                  </a>
                </p>
              ) : null}
              {error ? (
                <p className="tenant-switcher-error" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          ) : null}

          <Link
            to="/me/organisations"
            role="menuitem"
            className="workspace-account-link"
            onClick={() => setOpen(false)}
          >
            <Icon name="settings" size="1.1rem" />
            <span>{t('tenancy.switcher.manage')}</span>
          </Link>
        </div>
      ) : null}
    </div>
  );
}
